import { useCallback } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Note } from "../../api/types/notesTypes";
import { useNotesContext } from "../notes/NotesContext";
import NotesList from "../notes/NotesList";
import EntitySection from "./EntitySection";
import EntitySectionHeader from "./EntitySectionHeader";

interface Props {
  collapsible?: boolean;
}

const EntityNotesSection = ({ collapsible }: Props) => {
  const [expanded, toggleExpanded] = useToggleState(!collapsible);

  const { entityType, entityId, objectType } = useNotesContext();

  const getNotes = useCallback(
    async () => adminApi.getNotes(entityType, entityId, objectType),
    [entityId, entityType, objectType]
  );

  const [notes, fetchError, { setData: setNotes }] = useFetch(getNotes);

  if (fetchError) {
    logError(fetchError, "[NotesSection]");
    return <DataLoadingFailed title="Failed to load notes" />;
  }

  const handleNoteCreated = (note: Note) => {
    setNotes((prev) => (prev ? [note, ...prev] : [note]));
  };

  const handleNoteUpdated = (note: Note) => {
    setNotes((prev) => (prev ? prev.map((n) => (n.id === note.id ? note : n)) : [note]));
  };

  const handleNoteDeleted = (noteId: string) => {
    setNotes((prev) => (prev ? prev.filter((n) => n.id !== noteId) : []));
  };

  return (
    <EntitySection isLoading={notes === undefined}>
      <EntitySectionHeader
        title={notes !== undefined && collapsible ? `Notes (${notes.length})` : "Notes"}
        expanded={expanded}
        onToggleExpanded={collapsible ? toggleExpanded : undefined}
      />
      {notes !== undefined && expanded && (
        <NotesList
          notes={notes}
          onCreated={handleNoteCreated}
          onUpdated={handleNoteUpdated}
          onDeleted={handleNoteDeleted}
        />
      )}
    </EntitySection>
  );
};

export default EntityNotesSection;
